
import React from 'react'
import Slider from 'react-slick'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Dialog, DialogContent, IconButton } from '@mui/material'
import { Close } from '@mui/icons-material'
import { compact, isEmpty, map } from '@/utils/lodash'

function SliderArrowNext(props) {
  const { className, onClick } = props
  return (
    <div className={className} onClick={onClick}>
      <FontAwesomeIcon icon={['fas', 'chevron-right']} />
    </div>
  )
}

function SliderArrowPrev(props) {
  const { className, onClick } = props
  return (
    <div className={className} onClick={onClick}>
      <FontAwesomeIcon icon={['fas', 'chevron-left']} />
    </div>
  )
}

const sliderSettings = {
  dots: true,
  fade: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: true,
  adaptiveHeight: false,
  nextArrow: <SliderArrowNext />,
  prevArrow: <SliderArrowPrev />,
}

/**
 * Full-size image carousel for public marketplace photos.
 *
 * Stage is filled with width/height 100% + object-fit: contain so photos
 * upscale to the modal (not stuck at native pixel size). Letterboxing is
 * only for aspect-ratio mismatch — not unused empty modal chrome.
 */
export default function PublicImageLightbox({
  open,
  onClose,
  images = [],
  initialIndex = 0,
}) {
  const slides = compact(images)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      aria-label="Property images"
      slotProps={{
        paper: {
          sx: {
            position: 'relative',
            backgroundColor: '#0b0b0b !important',
            backgroundImage: 'none',
            color: 'white',
            m: { xs: 1, sm: 2 },
            width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 32px)' },
            maxHeight: '96vh',
          },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="Close image gallery"
        size="large"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 2,
          color: '#fff',
          backgroundColor: 'rgba(0, 0, 0, 0.65)',
          border: '1px solid rgba(255, 255, 255, 0.55)',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.45)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
          },
        }}
      >
        <Close />
      </IconButton>
      <DialogContent
        sx={{
          p: { xs: 0.5, sm: 1 },
          pb: 4,
          overflow: 'hidden',
          backgroundColor: '#0b0b0b',
          // Keep slick track/slide from constraining the stage
          '& .slick-list, & .slick-track, & .slick-slide > div': {
            height: '100%',
          },
          '& .slick-slide img': {
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          },
        }}
      >
        {!isEmpty(slides) ? (
          <Box sx={{ height: { xs: '72vh', sm: '82vh' } }}>
            <Slider key={initialIndex} {...sliderSettings} initialSlide={initialIndex} sx={{
              "& .slick-dots li button": { background: "rgba(255, 255, 255, 0.3)" },
              "& .slick-dots li.slick-active button": { background: "white" },
            }}>
              {map(slides, (image) => (
                <div key={image.url || image.url_small}>
                  <Box
                    sx={{
                      height: { xs: '72vh', sm: '82vh' },
                      width: '100%',
                      backgroundColor: '#0b0b0b',
                    }}
                  >
                    <Box
                      component="img"
                      src={image.url || image.url_small}
                      alt="Property"
                      sx={{
                        // Fill the stage and upscale when needed; contain
                        // preserves aspect ratio (letterbox only for shape).
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        display: 'block',
                      }}
                    />
                  </Box>
                </div>
              ))}
            </Slider>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
